// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Plan bearbeiten (nur eigene Pläne): Kopf, Tage, Übungen, Timer.
// Commit-on-Blur-Muster aus kraftbogen/PlanEditPage, an das fitHub-Schema angepasst.
import { useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { pb } from "@/lib/pb"
import { getPlans, getPlanDays, getPlanDayItems, getExercises, onSynced, syncFromPocketBase } from "@/lib/data"
import { BLOCK_DE } from "@/lib/labels"
import { toast } from "@/lib/toast"
import type { Exercise, Plan, PlanDay, PlanDayItem, TimerConfig } from "@/lib/types"
import ExercisePicker from "@/components/plans/ExercisePicker"
import Eule from "@/components/celebration/creatures/Eule"
import { IconStoppuhr } from "@/components/icons"
import "@/styles/planedit.css"

const bySort = (a: { sort: number }, b: { sort: number }) => a.sort - b.sort

function netErr() {
  toast("Dafür brauchst du Internet – bitte später erneut versuchen")
}

export default function PlanEditPage() {
  const { id = "" } = useParams<{ id: string }>()
  const [loaded, setLoaded] = useState(false)
  const [plan, setPlan] = useState<Plan | null>(null)
  const [days, setDays] = useState<PlanDay[]>([])
  const [items, setItems] = useState<PlanDayItem[]>([])
  const [exercises, setExercises] = useState<Exercise[]>([])

  useEffect(() => {
    const load = async () => {
      const [plans, allDays, allItems, exs] = await Promise.all([
        getPlans(), getPlanDays(), getPlanDayItems(), getExercises(),
      ])
      setPlan(plans.find((p) => p.id === id) ?? null)
      setDays(allDays.filter((d) => d.plan === id).sort(bySort))
      setItems(allItems)
      setExercises(exs)
      setLoaded(true)
    }
    void load()
    return onSynced(() => { void load() })
  }, [id])

  // Beim Verlassen einmal mit dem Server abgleichen (nicht nach jedem Tastendruck).
  useEffect(() => () => { void syncFromPocketBase() }, [])

  const exMap = useMemo(() => new Map(exercises.map((x) => [x.id, x])), [exercises])

  // ── Plan-Kopf ──
  async function commitPlan(patch: Partial<Pick<Plan, "name" | "description">>) {
    if (!plan) return
    try {
      await pb.collection("plans").update(plan.id, patch)
      setPlan({ ...plan, ...patch })
      toast("Gespeichert ✓")
    } catch { netErr() }
  }

  // ── Tage ──
  async function addDay() {
    if (!plan) return
    const used = new Set(days.map((d) => d.code))
    let code = "A"
    for (let c = 65; c <= 90; c++) {
      const letter = String.fromCharCode(c)
      if (!used.has(letter)) { code = letter; break }
    }
    const sort = Math.max(0, ...days.map((d) => d.sort)) + 1
    try {
      const created = await pb.collection("plan_days").create<PlanDay>({
        plan: plan.id, code, name: "", sort,
      })
      setDays((cur) => [...cur, created].sort(bySort))
    } catch { netErr() }
  }

  async function commitDay(day: PlanDay, patch: Partial<PlanDay>) {
    try {
      await pb.collection("plan_days").update(day.id, patch)
      setDays((cur) => cur.map((d) => (d.id === day.id ? { ...d, ...patch } : d)))
    } catch { netErr() }
  }

  async function moveDay(index: number, dir: -1 | 1) {
    const a = days[index]
    const b = days[index + dir]
    if (!a || !b) return
    try {
      await Promise.all([
        pb.collection("plan_days").update(a.id, { sort: b.sort }),
        pb.collection("plan_days").update(b.id, { sort: a.sort }),
      ])
      setDays((cur) => cur
        .map((d) => (d.id === a.id ? { ...d, sort: b.sort } : d.id === b.id ? { ...d, sort: a.sort } : d))
        .sort(bySort))
    } catch { netErr() }
  }

  // Bestätigung inline im DayCard — confirm() wird in der installierten App verschluckt
  async function removeDay(day: PlanDay) {
    try {
      await pb.collection("plan_days").delete(day.id)
      setDays((cur) => cur.filter((d) => d.id !== day.id))
      setItems((cur) => cur.filter((i) => i.plan_day !== day.id))
    } catch { netErr() }
  }

  // ── Übungen im Tag ──
  async function addExercise(day: PlanDay, ex: Exercise) {
    const siblings = items.filter((i) => i.plan_day === day.id)
    const sort = Math.max(0, ...siblings.map((i) => i.sort)) + 1
    try {
      const created = await pb.collection("plan_day_items").create<PlanDayItem>({
        plan_day: day.id, exercise: ex.id, sort, block: "strength",
        target_sets: ex.default_sets || 3, target_reps: ex.default_reps || "10–12", timer: null,
      })
      setItems((cur) => [...cur, created])
      toast(`${ex.name} hinzugefügt ✓`)
    } catch { netErr() }
  }

  async function commitItem(item: PlanDayItem, patch: Partial<PlanDayItem>) {
    try {
      await pb.collection("plan_day_items").update(item.id, patch)
      setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, ...patch } : i)))
    } catch { netErr() }
  }

  async function moveItem(item: PlanDayItem, dir: -1 | 1) {
    const siblings = items.filter((i) => i.plan_day === item.plan_day).sort(bySort)
    const idx = siblings.findIndex((i) => i.id === item.id)
    const other = siblings[idx + dir]
    if (!other) return
    try {
      await Promise.all([
        pb.collection("plan_day_items").update(item.id, { sort: other.sort }),
        pb.collection("plan_day_items").update(other.id, { sort: item.sort }),
      ])
      setItems((cur) => cur.map((i) =>
        i.id === item.id ? { ...i, sort: other.sort } : i.id === other.id ? { ...i, sort: item.sort } : i))
    } catch { netErr() }
  }

  async function removeItem(item: PlanDayItem) {
    try {
      await pb.collection("plan_day_items").delete(item.id)
      setItems((cur) => cur.filter((i) => i.id !== item.id))
    } catch { netErr() }
  }

  // ── Render ──
  if (!loaded) return null
  if (!plan) {
    return (
      <div className="empty">
        <div className="critter"><Eule /></div>
        <p><b>Diesen Plan gibt es nicht.</b></p>
        <Link to="/plaene" className="btn-secondary pressable">Zurück zu den Plänen</Link>
      </div>
    )
  }
  if (plan.owner !== pb.authStore.record?.id) {
    return (
      <div className="empty">
        <div className="critter"><Eule /></div>
        <p><b>Nur eigene Pläne lassen sich bearbeiten.</b></p>
        <Link to="/plaene" className="btn-secondary pressable">Zurück zu den Plänen</Link>
      </div>
    )
  }

  return (
    <>
      {/* klebt beim Scrollen — jederzeit zurück (wie Plan-Ansicht);
          die Überschrift „Plan bearbeiten" steht jetzt im Seitenkopf */}
      <p className="plan-back"><Link to="/plaene">‹ Alle Pläne</Link></p>

      <div className="card">
        <label htmlFor="pe-name">Name</label>
        <input
          id="pe-name" type="text" defaultValue={plan.name}
          onBlur={(e) => {
            const v = e.target.value.trim()
            if (v && v !== plan.name) void commitPlan({ name: v })
          }}
        />
        <label htmlFor="pe-desc">Beschreibung</label>
        <textarea
          id="pe-desc" rows={2} defaultValue={plan.description}
          placeholder="Wofür ist dieser Plan?"
          onBlur={(e) => {
            const v = e.target.value.trim()
            if (v !== plan.description) void commitPlan({ description: v })
          }}
        />
      </div>

      <h2>Trainingstage</h2>
      {days.length === 0 && <p className="hint">Noch keine Tage – lege mit „+ Tag" los.</p>}
      {days.map((day, i) => (
        <DayCard
          key={day.id}
          day={day}
          dayItems={items.filter((it) => it.plan_day === day.id).sort(bySort)}
          exercises={exercises}
          exMap={exMap}
          first={i === 0}
          last={i === days.length - 1}
          onCommitDay={(patch) => commitDay(day, patch)}
          onMoveDay={(dir) => void moveDay(i, dir)}
          onRemoveDay={() => void removeDay(day)}
          onCommitItem={commitItem}
          onMoveItem={(it, dir) => void moveItem(it, dir)}
          onRemoveItem={(it) => void removeItem(it)}
          onAddExercise={(ex) => void addExercise(day, ex)}
        />
      ))}
      <button type="button" className="btn-secondary pe-addday" onClick={() => void addDay()}>+ Tag</button>

      <p className="hint">Änderungen werden automatisch gespeichert.</p>
      <p className="pe-back"><Link to="/plaene">← Zurück zu den Plänen</Link></p>
    </>
  )
}

// ── Tages-Karte ──
interface DayCardProps {
  day: PlanDay
  dayItems: PlanDayItem[]
  exercises: Exercise[]
  exMap: Map<string, Exercise>
  first: boolean
  last: boolean
  onCommitDay: (patch: Partial<PlanDay>) => Promise<void>
  onMoveDay: (dir: -1 | 1) => void
  onRemoveDay: () => void
  onCommitItem: (item: PlanDayItem, patch: Partial<PlanDayItem>) => Promise<void>
  onMoveItem: (item: PlanDayItem, dir: -1 | 1) => void
  onRemoveItem: (item: PlanDayItem) => void
  onAddExercise: (ex: Exercise) => void
}

function DayCard({
  day, dayItems, exercises, exMap, first, last,
  onCommitDay, onMoveDay, onRemoveDay, onCommitItem, onMoveItem, onRemoveItem, onAddExercise,
}: DayCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)

  return (
    <div className="card pe-day">
      <div className="pe-day-head">
        <input
          className="pe-code" type="text" maxLength={2} aria-label="Kürzel des Tags"
          defaultValue={day.code}
          onBlur={(e) => {
            const v = e.target.value.trim()
            if (v && v !== day.code) void onCommitDay({ code: v })
          }}
        />
        <input
          className="pe-dayname" type="text" aria-label="Name des Tags" placeholder="z. B. Oberkörper"
          defaultValue={day.name}
          onBlur={(e) => {
            const v = e.target.value.trim()
            if (v !== day.name) void onCommitDay({ name: v })
          }}
        />
        <button type="button" className="pe-icon" aria-label="Tag nach oben" disabled={first} onClick={() => onMoveDay(-1)}>↑</button>
        <button type="button" className="pe-icon" aria-label="Tag nach unten" disabled={last} onClick={() => onMoveDay(1)}>↓</button>
        <button type="button" className="pe-icon danger" aria-label="Tag löschen" onClick={() => setConfirmDel(true)}>✕</button>
      </div>

      {confirmDel && (
        /* Destruktives = Inline-Zweischritt, nie confirm() (Skill §13.6) */
        <div className="pe-del-confirm">
          <span>Tag und alle Übungen darin löschen?</span>
          <button type="button" className="btn-secondary" onClick={() => setConfirmDel(false)}>Behalten</button>
          <button type="button" className="btn-secondary del-yes" onClick={onRemoveDay}>Ja, löschen</button>
        </div>
      )}

      {dayItems.map((item, i) => (
        <ItemRow
          key={item.id}
          item={item}
          exercise={exMap.get(item.exercise)}
          first={i === 0}
          last={i === dayItems.length - 1}
          onCommit={(patch) => onCommitItem(item, patch)}
          onMove={(dir) => onMoveItem(item, dir)}
          onRemove={() => onRemoveItem(item)}
        />
      ))}

      <button type="button" className="pe-addex" onClick={() => setPickerOpen((o) => !o)}>
        {pickerOpen ? "Auswahl schließen" : "+ Übung"}
      </button>
      {pickerOpen && <ExercisePicker exercises={exercises} onPick={onAddExercise} />}
    </div>
  )
}

// ── Übungszeile ──
interface ItemRowProps {
  item: PlanDayItem
  exercise: Exercise | undefined
  first: boolean
  last: boolean
  onCommit: (patch: Partial<PlanDayItem>) => Promise<void>
  onMove: (dir: -1 | 1) => void
  onRemove: () => void
}

function ItemRow({ item, exercise, first, last, onCommit, onMove, onRemove }: ItemRowProps) {
  const [timerOpen, setTimerOpen] = useState(false)
  const [timer, setTimer] = useState<TimerConfig>(item.timer ?? { rounds: 3, work_sec: 30, rest_sec: 15 })

  function commitTimer() {
    const t: TimerConfig = {
      rounds: Math.max(1, timer.rounds || 1),
      work_sec: Math.max(1, timer.work_sec || 1),
      rest_sec: Math.max(0, timer.rest_sec || 0),
    }
    setTimer(t)
    if (JSON.stringify(t) !== JSON.stringify(item.timer)) void onCommit({ timer: t })
  }

  return (
    <div className="pe-item">
      <div className="pe-item-main">
        {exercise?.media_slug && (
          <img
            src={`/media/exercises/${exercise.media_slug}.jpg`}
            alt="" loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none" }}
          />
        )}
        <span className="pe-item-name">{exercise?.name ?? "Unbekannte Übung"}</span>
        <button type="button" className="pe-icon" aria-label="Übung nach oben" disabled={first} onClick={() => onMove(-1)}>↑</button>
        <button type="button" className="pe-icon" aria-label="Übung nach unten" disabled={last} onClick={() => onMove(1)}>↓</button>
        <button type="button" className="pe-icon danger" aria-label="Übung entfernen" onClick={onRemove}>✕</button>
      </div>

      <div className="pe-item-fields">
        <select
          aria-label="Block" value={item.block}
          onChange={(e) => void onCommit({ block: e.target.value as PlanDayItem["block"] })}
        >
          <option value="">— Block —</option>
          {Object.entries(BLOCK_DE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input
          className="pe-sets" type="number" min={1} aria-label="Sätze"
          defaultValue={item.target_sets}
          onBlur={(e) => {
            const n = Number(e.target.value)
            if (n >= 1 && n !== item.target_sets) void onCommit({ target_sets: n })
          }}
        />
        <input
          className="pe-reps" type="text" aria-label="Wiederholungen"
          defaultValue={item.target_reps} placeholder="z. B. 10–12"
          onBlur={(e) => {
            const v = e.target.value.trim()
            if (v !== item.target_reps) void onCommit({ target_reps: v })
          }}
        />
      </div>

      {/* C6: Pausen je Übung — leer = Profil-Standard bzw. keine Übungspause */}
      <div className="pe-pausen">
        <label>Pause zwischen Sätzen (s)
          <input
            type="number" min={0} max={600} inputMode="numeric"
            defaultValue={item.rest_sec || ""}
            placeholder={`Standard: ${Number(pb.authStore.record?.default_rest_sec) || 90}`}
            onBlur={(e) => {
              const n = Math.max(0, Number(e.target.value) || 0)
              if (n !== (item.rest_sec || 0)) void onCommit({ rest_sec: n })
            }}
          />
        </label>
        <label>Pause nach der Übung (s)
          <input
            type="number" min={0} max={600} inputMode="numeric"
            defaultValue={item.pause_after_sec || ""}
            placeholder="keine"
            onBlur={(e) => {
              const n = Math.max(0, Number(e.target.value) || 0)
              if (n !== (item.pause_after_sec || 0)) void onCommit({ pause_after_sec: n })
            }}
          />
        </label>
      </div>

      <button type="button" className="pe-timer-toggle" onClick={() => setTimerOpen((o) => !o)}>
        {item.timer
          ? <><IconStoppuhr size={15} /> {`${item.timer.rounds} × ${item.timer.work_sec} s Arbeit / ${item.timer.rest_sec} s Pause`}</>
          : "Timer"} {timerOpen ? "▴" : "▾"}
      </button>
      {timerOpen && (
        <div className="pe-timer">
          <label>Runden
            <input
              type="number" min={1} value={timer.rounds}
              onChange={(e) => setTimer({ ...timer, rounds: Number(e.target.value) })}
              onBlur={commitTimer}
            />
          </label>
          <label>Arbeit (Sek.)
            <input
              type="number" min={1} value={timer.work_sec}
              onChange={(e) => setTimer({ ...timer, work_sec: Number(e.target.value) })}
              onBlur={commitTimer}
            />
          </label>
          <label>Pause (Sek.)
            <input
              type="number" min={0} value={timer.rest_sec}
              onChange={(e) => setTimer({ ...timer, rest_sec: Number(e.target.value) })}
              onBlur={commitTimer}
            />
          </label>
          <button
            type="button" className="pe-timer-remove"
            onClick={() => { void onCommit({ timer: null }); setTimerOpen(false) }}
          >
            Timer entfernen
          </button>
        </div>
      )}
    </div>
  )
}
